/**
 * PM_People DateTime Utility
 *
 * RULE: All timestamps from the backend are UTC (stored in DB as UTC).
 *       Some columns are 'timestamp without time zone' — they come back as ISO
 *       strings with a server-local offset (+05:30) or without any suffix.
 *       Strings that already carry an offset/Z are parsed as-is.
 *       Strings with NO offset are assumed to be UTC and receive a 'Z' suffix.
 *
 * RULE: Convert to the user's local timezone ONLY at render time.
 * RULE: Never hardcode a timezone — always use the browser's detected timezone.
 * RULE: Never send a local datetime string to the backend.
 *       The backend generates all server-side timestamps with DateTime.UtcNow.
 */

/** User's detected IANA timezone — computed once at module load. */
export const USER_TIMEZONE: string =
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Parse a backend date/datetime string as UTC.
 *
 * Rationale: 'timestamp without time zone' DB columns return ISO strings that
 * may carry the server's local offset (+05:30) or no offset at all.
 *   - With offset ("+05:30" / "Z")  → JavaScript Date() handles it correctly.
 *   - Without offset ("2026-05-19T04:47:02.157033") → JS treats as LOCAL time
 *     which is wrong if the stored value is UTC. Appending 'Z' forces UTC.
 *
 * @param value  Raw ISO string from backend API response (or null/undefined).
 * @returns Correct UTC Date object, or an invalid Date for null/empty input.
 */
export function parseBackendDate(value: string | null | undefined): Date {
  if (!value) return new Date(NaN);
  // Already has timezone info — parse as-is
  if (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value)) {
    return new Date(value);
  }
  // No timezone designator — assume UTC (backend stores UTC)
  return new Date(value + 'Z');
}

/**
 * Return today's date as a YYYY-MM-DD string in the user's LOCAL timezone.
 *
 * Rationale: new Date().toISOString() gives the UTC date, which differs from
 * the local date near midnight (e.g. 11:45 PM IST = next day in UTC).
 */
export function localTodayString(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Normalise a backend date string (possibly "2026-05-19T00:00:00") to
 * "YYYY-MM-DD" by stripping the time portion.
 */
export function normalizeDateString(value: string): string {
  return value.split('T')[0];
}

/**
 * Format a UTC datetime string as local time (HH:MM AM/PM).
 * Used for: punch-in time, punch-out time display.
 *
 * @param value  ISO 8601 string (with or without timezone designator).
 * @returns Local time string e.g. "10:00 AM", or "—" for null/invalid.
 */
export function formatLocalTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = parseBackendDate(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: USER_TIMEZONE,
    });
  } catch {
    return '—';
  }
}

/**
 * Format a UTC datetime string as local date (DD MMM YYYY).
 * Used for: attendance date display.
 *
 * @param value  ISO 8601 string.
 * @returns Local date string e.g. "19 May 2026", or "—" for null/invalid.
 */
export function formatLocalDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = parseBackendDate(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: USER_TIMEZONE,
    });
  } catch {
    return '—';
  }
}

/**
 * Format a UTC datetime string as local date + time.
 * Used for: full timestamp display in logs.
 *
 * @param value  ISO 8601 string.
 * @returns e.g. "19 May 2026, 10:00 AM", or "—" for null/invalid.
 */
export function formatLocalDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = parseBackendDate(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: USER_TIMEZONE,
    });
  } catch {
    return '—';
  }
}

/**
 * Format a UTC datetime string as a relative time ("5m ago").
 * Used for: notification timestamps.
 *
 * Falls back to an absolute local date past a week — "63d ago" helps nobody.
 *
 * @param value  ISO 8601 string.
 * @returns e.g. "Just now", "5m ago", "3h ago", "2d ago", "19 May 2026".
 */
export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = parseBackendDate(value);
  if (isNaN(d.getTime())) return '—';

  const seconds = (Date.now() - d.getTime()) / 1000;
  if (seconds < 60) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days <= 7) return `${days}d ago`;

  return formatLocalDate(value);
}

/**
 * Format a Date object as local time (HH:MM AM/PM).
 * Accepts a Date directly (unlike formatLocalTime which takes a string).
 * Always uses the browser-detected USER_TIMEZONE — never reads localStorage.
 *
 * @param date  Date object (typically from parseBackendDate).
 * @returns Local time string e.g. "04:51 AM", or "—" for null/invalid.
 */
export function formatTimeFromDate(date: Date | null | undefined): string {
  if (!date || isNaN(date.getTime())) return '—';
  try {
    var todaysDate = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: USER_TIMEZONE,
    });
    return todaysDate;
  } catch {
    return '—';
  }
}
